import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import VideoContainer from '/components/VideoContainer/VideoContainer'; // Import the VideoContainer component
import sectionStyles from '/components/Sections/SectionHome.module.css'; // Use sectionStyles specific to the home page
import infoPaneStyles from '/components/Panes/InfoPane.module.css'; // Import InfoPane styles
import Accordion from '/components/Accordions/Accordion'; // Import the Accordion component
import pool from '/lib/db';

const sitemapGroups = [
  {
    topic: 'Explore the library',
    description: 'Browse the six fixed content types or follow their relationships.',
    links: [
      ['/Positions', 'Positions', 'Foundational body placements'],
      ['/Moves', 'Moves', 'Individual movement ideas'],
      ['/SimpleCycles', 'Simple Cycles', 'Repeatable movement cycles'],
      ['/Transitions', 'Transitions', 'Connections between movements'],
      ['/Combinations', 'Combinations', 'Structured groups of movements'],
      ['/Choreography', 'Choreography', 'Complete arranged works'],
      ['/Mindmap', 'Shuffle MindMap', 'Explore names and relationships visually'],
    ],
  },
  {
    topic: 'Learn and organize',
    description: 'Turn the reference library into practice and personal collections.',
    links: [
      ['/Exercises', 'Exercises', 'Practice with focused activities'],
      ['/Lists', 'Lists', 'Collect content around a purpose'],
      ['/Sequences', 'Sequences', 'Arrange content into an order'],
      ['/Leaderboard', 'Leaderboard', 'See community contribution activity'],
      ['/Contributors', 'Contributors', 'Meet the people improving ShuffleDB'],
    ],
  },
  {
    topic: 'Missions and contribution',
    description: 'Find useful work, contribute knowledge, and follow review progress.',
    links: [
      ['/Missions', 'Browse Missions', 'Choose an available contribution'],
      ['/ContributeContent', 'Propose Content', 'Suggest a new library entry'],
      ['/Profile', 'Mission History', 'Resume drafts and review your activity'],
      ['/MissionCreator', 'Create a Mission', 'Turn a database need into guided work'],
      ['/MissionReview', 'Review Submissions', 'Evaluate submitted contributions'],
      ['/CategoriesList', 'Manage Categories', 'Maintain optional category structures'],
    ],
  },
  {
    topic: 'Creative toolchain',
    description: 'Capture, analyze, edit, publish, and discover across the wider platform.',
    links: [
      ['/ShuffleMaps', 'ShuffleMaps', 'Discover scenes, events, classes, and dancers'],
      ['/CapDat', 'CapDat', 'Capture source media and evidence'],
      ['/TrackDat', 'TrackDat', 'Build movement-aware visuals'],
      ['/PostDat', 'PostDat', 'Prepare and publish finished work'],
    ],
  },
  {
    topic: 'About the project',
    description: 'Understand the goals, architecture, progress, and project policies.',
    links: [
      ['/OurVision', 'Our Vision', 'Why ShuffleDB is being built'],
      ['/TechnologyStack', 'Technology Stack', 'How the platform is constructed'],
      ['/ShuffleDBSandbox', 'ShuffleDB Sandbox', 'Current work, verification, and next steps'],
      ['/PrivacyPolicy', 'Privacy Policy', 'How information is handled'],
      ['/TermsOfService', 'Terms of Service', 'Rules for using the platform'],
      ['/DMCA', 'Copyright and DMCA', 'Copyright reporting information'],
    ],
  },
  {
    topic: 'Administration',
    description: 'Permission-protected tools for maintaining the platform.',
    restricted: true,
    links: [
      ['/ContentAdmin', 'Content Admin', 'Edit, review, and publish library records'],
      ['/CategoryCreation', 'Create a Category', 'Add an optional classification'],
      ['/ExerciseAdmin', 'Exercise Admin', 'Maintain learning exercises'],
      ['/UserManagement', 'User Management', 'Manage member accounts'],
      ['/RoleManagement', 'Roles and Permissions', 'Control administrative access'],
      ['/SubscribersAdmin', 'Email Subscribers', 'Review captured subscriptions'],
      ['/EmailOutboxAdmin', 'Email Outbox', 'Inspect queued email delivery'],
    ],
  },
];

const homepageAccordionOrder = [
  'Getting Started: How it works.',
  'About the project',
  'A systems solution to these problems.',
  'Sitemap: Explore by topic.',
];

const missionContributionSlides = [
  {
    src: '/images/mission-guide/complete-required-step.png',
    alt: 'The Mission Taker Description tab with existing information and a completed proposed description',
    label: 'Write',
    title: 'Explain the movement in clear, reviewable language.',
  },
  {
    src: '/images/mission-guide/upload-media-evidence.png',
    alt: 'The Mission Taker Media tab showing two uploaded gallery images saved to the mission draft',
    label: 'Upload',
    title: 'Add images and see that the media is safely attached.',
  },
  {
    src: '/images/mission-guide/add-reference-links.png',
    alt: 'The Mission Taker Media tab with completed reference links for front and angled perspectives',
    label: 'Reference',
    title: 'Connect videos and sources reviewers can compare.',
  },
];

function MissionContributionSlideshow() {
  const [activeSlide, setActiveSlide] = React.useState(0);
  const slide = missionContributionSlides[activeSlide];
  const move = (direction) => setActiveSlide((current) => (
    current + direction + missionContributionSlides.length
  ) % missionContributionSlides.length);

  return (
    <div
      className={`${sectionStyles.walkthroughImage} ${sectionStyles.walkthroughSlideshow}`}
      role="region"
      aria-roledescription="carousel"
      aria-label="Examples of contributing through the Mission Taker"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') move(-1);
        if (event.key === 'ArrowRight') move(1);
      }}
    >
      <Link
        href="/MissionTaker?hash=43e3bb44-ac7d-11ef-8f41-040300000000"
        className={sectionStyles.walkthroughSlideImage}
        aria-label={`Open the Mission Taker: ${slide.title}`}
      >
        <Image
          key={slide.src}
          src={slide.src}
          alt={slide.alt}
          width={1420}
          height={490}
          sizes="(max-width: 760px) 100vw, 64vw"
        />
      </Link>
      <div className={sectionStyles.walkthroughSlideControls}>
        <button type="button" onClick={() => move(-1)} aria-label="Previous contribution example">‹</button>
        <div className={sectionStyles.walkthroughSlideCaption} aria-live="polite">
          <span>{slide.label}</span>
          <strong>{slide.title}</strong>
        </div>
        <div className={sectionStyles.walkthroughSlideDots} aria-label={`Slide ${activeSlide + 1} of ${missionContributionSlides.length}`}>
          {missionContributionSlides.map((item, index) => (
            <button
              key={item.src}
              type="button"
              className={index === activeSlide ? sectionStyles.activeSlideDot : ''}
              onClick={() => setActiveSlide(index)}
              aria-label={`Show example ${index + 1}: ${item.label}`}
              aria-current={index === activeSlide ? 'true' : undefined}
            />
          ))}
        </div>
        <button type="button" onClick={() => move(1)} aria-label="Next contribution example">›</button>
      </div>
    </div>
  );
}

const Home = ({ username, contentCounts }) => (
  <>
    {/* Main Content with the specific section styles for the home page */}
    <section id="home" className={`${sectionStyles.section} ${sectionStyles.sectionActive}`}>
      {/* Info Pane */}
      <div className={`${infoPaneStyles.infoPane} ${sectionStyles.homeIntro}`}>
        <h1>
          Welcome to ShuffleDB.com
          {username && (
            <>
              , <Link href="/UserProfile" className={infoPaneStyles.infoPaneLink}>{username}</Link>.
            </>
          )}
        </h1>

        <h2>
          Your comprehensive crowdsourced compendium for shuffle dance{' '}
          <Link href="/Positions" className={infoPaneStyles.infoPaneLink}>positions ({contentCounts.Position})</Link>,{' '}
          <Link href="/Moves" className={infoPaneStyles.infoPaneLink}>moves ({contentCounts.Move})</Link>,{' '}
          <Link href="/SimpleCycles" className={infoPaneStyles.infoPaneLink}>simple cycles ({contentCounts['Simple Cycle']})</Link>,{' '}
          <Link href="/Transitions" className={infoPaneStyles.infoPaneLink}>transitions ({contentCounts.Transition})</Link>,{' '}
          <Link href="/Combinations" className={infoPaneStyles.infoPaneLink}>combinations ({contentCounts.Combination})</Link>, and{' '}
          <Link href="/Choreography" className={infoPaneStyles.infoPaneLink}>choreography ({contentCounts.Choreography})</Link>.
        </h2>
      </div>

      {/* Video Container Component */}
      <VideoContainer />

      {/* Full-width accordion content */}
      <div className={sectionStyles.pageContainer}>
        {/* Accordions */}
        <div className={sectionStyles.mainContent}>
          <Accordion
            defaultOpenIndex={0}
            singleOpen
            autoOpenOnScroll
            items={[
              {
                header: 'Getting Started: How it works.',
                content: (
                  <div className={`accordion-interior ${sectionStyles.missionGuide}`}>
                    <h1>Explore the library. Improve it through missions.</h1>
                    <p>
                      ShuffleDB is a community-powered reference for shuffle dance positions, moves, transitions, combinations, and choreography. Anyone can browse the library and learning tools. Signed-in contributors can take missions that request specific additions or improvements to the database.
                    </p>

                    <h1>Start with a category—or follow the connections.</h1>
                    <p>
                      ShuffleDB has only six fixed content types—the choices in the upper-left dropdown. Browse one of those collections when you know what you want, or open Shuffle MindMap to discover how names, community classifications, and related movements connect across them.
                    </p>

                    <nav className={sectionStyles.contentFamilyLinks} aria-label="ShuffleDB content families">
                      <Link href="/Positions"><span>01</span>Positions</Link>
                      <Link href="/Moves"><span>02</span>Moves</Link>
                      <Link href="/SimpleCycles"><span>03</span>Simple Cycles</Link>
                      <Link href="/Transitions"><span>04</span>Transitions</Link>
                      <Link href="/Combinations"><span>05</span>Combinations</Link>
                      <Link href="/Choreography"><span>06</span>Choreography</Link>
                    </nav>

                    <div className={`${sectionStyles.missionWalkthrough} ${sectionStyles.libraryWalkthrough}`} aria-label="Library interface examples">
                      <article className={sectionStyles.walkthroughCard}>
                        <div className={sectionStyles.walkthroughCopy}>
                          <span>EXPLORE · CATEGORY VIEW</span>
                          <h2>Move from a collection to the details.</h2>
                          <p>
                            Content-type pages keep related records together without forcing them into one style or school. Open an item to study it, switch collections from Quick Access, propose a missing entry, or find missions connected to that content type.
                          </p>
                          <div className={sectionStyles.walkthroughActions}>
                            <Link href="/Moves">Browse the move library →</Link>
                            <Link href="/Exercises">Practice with exercises</Link>
                          </div>
                        </div>
                        <Link href="/Moves" className={sectionStyles.walkthroughImage} aria-label="Open the shuffle dance moves library">
                          <Image
                            src="/images/library-guide/browse-move-library.png"
                            alt="The ShuffleDB move library showing expandable move records and the Quick Access panel"
                            width={1420}
                            height={720}
                            sizes="(max-width: 760px) 100vw, 64vw"
                          />
                        </Link>
                      </article>

                      <article className={`${sectionStyles.walkthroughCard} ${sectionStyles.walkthroughCardReverse}`}>
                        <div className={sectionStyles.walkthroughCopy}>
                          <span>EXPLORE · SHUFFLE MINDMAP</span>
                          <h2>See the vocabulary as a living system.</h2>
                          <p>
                            Shuffle MindMap turns library relationships into an interactive graph. Fit or zoom the map, adjust its spread, trace branches between related ideas, and use the hierarchy panel to move directly into a category or content record.
                          </p>
                          <div className={sectionStyles.walkthroughActions}>
                            <Link href="/Mindmap">Open Shuffle MindMap →</Link>
                            <Link href="/Lists">Build a personal list</Link>
                          </div>
                        </div>
                        <Link href="/Mindmap" className={sectionStyles.walkthroughImage} aria-label="Open Shuffle MindMap">
                          <Image
                            src="/images/library-guide/explore-shuffle-mindmap.png"
                            alt="Shuffle MindMap showing movement nodes connected to ShuffleDB with hierarchy navigation"
                            width={1420}
                            height={820}
                            sizes="(max-width: 760px) 100vw, 64vw"
                          />
                        </Link>
                      </article>
                    </div>

                    <h1>Taking your first mission.</h1>
                    <p>
                      Sign in, open <Link href="/Missions">Missions</Link>, and choose an available mission. Opening it temporarily reserves that mission while you work. The Mission Taker paces you through Naming, Description, Physics, and Media, clearly marking what is required and what is only suggested. Your changes are saved as a draft, so you can leave and return before submitting.
                    </p>

                    <div className={sectionStyles.missionWalkthrough} aria-label="Mission interface examples">
                      <article className={sectionStyles.walkthroughCard}>
                        <div className={sectionStyles.walkthroughCopy}>
                          <span>01 · Choose</span>
                          <h2>See the work before you start.</h2>
                          <p>
                            Expand an available mission to read its description, required fields, and suggested contributions before selecting Start Mission.
                          </p>
                          <Link href="/Missions">Browse active missions →</Link>
                        </div>
                        <Link href="/Missions" className={sectionStyles.walkthroughImage} aria-label="Open the available missions interface">
                          <Image
                            src="/images/mission-guide/choose-a-mission.png"
                            alt="An expanded available mission showing its description, required fields, and Start Mission button"
                            width={1014}
                            height={330}
                            sizes="(max-width: 760px) 100vw, 64vw"
                          />
                        </Link>
                      </article>

                      <article className={`${sectionStyles.walkthroughCard} ${sectionStyles.walkthroughCardReverse}`}>
                        <div className={sectionStyles.walkthroughCopy}>
                          <span>02 · Contribute</span>
                          <h2>Move through one focused step at a time.</h2>
                          <p>
                            The tabs separate Naming, Description, Physics, and Media. A typical mission may ask for a written explanation, image uploads, or links to useful source videos. Each contribution stays visible in the draft while required and suggested work is clearly labeled.
                          </p>
                          <Link href="/MissionTaker?hash=43e3bb44-ac7d-11ef-8f41-040300000000">Preview the Mission Taker →</Link>
                        </div>
                        <MissionContributionSlideshow />
                      </article>
                    </div>

                    <h1>Creating missions.</h1>
                    <p>
                      Mission managers use the <Link href="/MissionCreator">Mission Creator</Link> to turn a specific database need into a guided task. They choose the content being improved, define required and suggested contributions, request useful media perspectives, set the point value and availability, then preview the mission before publishing it. New missions remain private drafts until they are ready for contributors.
                    </p>

                    <h1>How a mission moves through ShuffleDB.</h1>
                    <div className={sectionStyles.missionFlow} aria-label="Mission lifecycle">
                      <div className={sectionStyles.flowLane}>
                        <div className={sectionStyles.flowRole}>Mission manager</div>
                        <div className={sectionStyles.flowStep}>
                          <span>01</span>
                          <strong>Create</strong>
                          <small>Define the need, fields, media, points, and schedule.</small>
                        </div>
                        <div className={sectionStyles.flowArrow} aria-hidden="true">→</div>
                        <div className={sectionStyles.flowStep}>
                          <span>02</span>
                          <strong>Publish</strong>
                          <small>Move the private draft into the available mission list.</small>
                        </div>
                      </div>

                      <div className={sectionStyles.flowLane}>
                        <div className={sectionStyles.flowRole}>Contributor</div>
                        <div className={sectionStyles.flowStep}>
                          <span>03</span>
                          <strong>Choose</strong>
                          <small>Open an available mission and reserve it while working.</small>
                        </div>
                        <div className={sectionStyles.flowArrow} aria-hidden="true">→</div>
                        <div className={sectionStyles.flowStep}>
                          <span>04</span>
                          <strong>Contribute</strong>
                          <small>Follow the guided required and suggested steps.</small>
                        </div>
                        <div className={sectionStyles.flowArrow} aria-hidden="true">→</div>
                        <div className={sectionStyles.flowStep}>
                          <span>05</span>
                          <strong>Submit</strong>
                          <small>Send the completed contribution to review.</small>
                        </div>
                      </div>

                      <div className={sectionStyles.flowLane}>
                        <div className={sectionStyles.flowRole}>Reviewer</div>
                        <div className={sectionStyles.flowStep}>
                          <span>06</span>
                          <strong>Review</strong>
                          <small>Compare the proposal with the established content.</small>
                        </div>
                        <div className={sectionStyles.flowBranch} aria-hidden="true">↙ &nbsp; ↘</div>
                        <div className={`${sectionStyles.flowStep} ${sectionStyles.flowRevision}`}>
                          <strong>Changes requested</strong>
                          <small>Reopen, revise, and resubmit without starting over.</small>
                        </div>
                        <div className={`${sectionStyles.flowStep} ${sectionStyles.flowApproved}`}>
                          <strong>Approved</strong>
                          <small>Update the library, award points, and record the result.</small>
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                header: 'A systems solution to these problems.',
                content: (
                  <div className={`accordion-interior ${sectionStyles.systemSolution}`}>
                    <div className={sectionStyles.solutionLead}>
                      <span>ONE STRUCTURE · MANY TRADITIONS</span>
                      <h1>Organized without declaring one style, form, school, or name to be the authority.</h1>
                      <p>
                        ShuffleDB is designed to be style-agnostic, form-agnostic, school-agnostic, and naming-convention-agnostic. Subcategorization is available, but it is never mandatory: the shared library can support different communities dividing and describing shuffle dance in different ways without turning one interpretation into the universal rule.
                      </p>
                    </div>

                    <section className={sectionStyles.fixedTypes}>
                      <div>
                        <span>THE ONLY HARD CATEGORIES</span>
                        <h2>The six content types in the upper-left dropdown.</h2>
                      </div>
                      <nav aria-label="ShuffleDB fixed content types">
                        <Link href="/Positions">Positions</Link>
                        <Link href="/Moves">Moves</Link>
                        <Link href="/SimpleCycles">Simple Cycles</Link>
                        <Link href="/Transitions">Transitions</Link>
                        <Link href="/Combinations">Combinations</Link>
                        <Link href="/Choreography">Choreography</Link>
                      </nav>
                    </section>

                    <section className={sectionStyles.perspectiveModel}>
                      <div className={sectionStyles.perspectiveModelIntro}>
                        <span>DESIGN GOAL</span>
                        <h1>One library. Many names and hierarchies.</h1>
                        <p>
                          Different dancers and communities may use different names for the same category or move, or organize the relationships between movements differently. ShuffleDB can preserve the shared underlying content while its MindMaps and visualizations expand to show multiple hierarchies instead of enforcing one vocabulary or arrangement.
                        </p>
                      </div>
                      <div className={sectionStyles.perspectiveSteps}>
                        <article><span>01</span><div><strong>Connect alternate names</strong><small>Let different category labels and move names lead to the same underlying content.</small></div></article>
                        <article><span>02</span><div><strong>Support different structures</strong><small>Allow communities to arrange related content differently without duplicating or redefining every record.</small></div></article>
                        <article><span>03</span><div><strong>Build multi-hierarchy maps</strong><small>Expand MindMaps and visualizations so users can explore and compare more than one valid organization.</small></div></article>
                      </div>
                    </section>

                    <div className={sectionStyles.agnosticPrinciples}>
                      <article>
                        <span>01</span>
                        <h2>Names can coexist</h2>
                        <p>A familiar name, regional name, school-specific term, alternative spelling, and previous name can all point to the same content without one being erased.</p>
                      </article>
                      <article>
                        <span>02</span>
                        <h2>Subcategories stay optional</h2>
                        <p>Styles, forms, schools, scenes, lineages, teaching systems, and community-created divisions may classify an item several ways—or leave it unclassified.</p>
                      </article>
                      <article>
                        <span>03</span>
                        <h2>Relationships carry meaning</h2>
                        <p>Parent content, related movements, component parts, sequences, and MindMap connections describe how an idea is used without claiming cultural ownership of it.</p>
                      </article>
                      <article>
                        <span>04</span>
                        <h2>Evidence stays visible</h2>
                        <p>Descriptions, contributors, media perspectives, references, and review history preserve context so users can compare variations instead of receiving one unexplained definition.</p>
                      </article>
                    </div>

                    <p className={sectionStyles.solutionClose}>
                      The goal is consistency in how information is stored—not a single mandatory taxonomy for how people dance or speak about dance. Missions and review improve clarity, coverage, and evidence; multi-hierarchy MindMaps and visualizations can make different names and organizational systems explorable without forcing them into one answer.
                    </p>
                  </div>
                ),
              },
              {
                header: 'Sitemap: Explore by topic.',
                content: (
                  <div className={`accordion-interior ${sectionStyles.sitemap}`}>
                    <div className={sectionStyles.sitemapLead}>
                      <span>SHUFFLEDB DIRECTORY</span>
                      <h1>Find the part of the platform you need.</h1>
                      <p>Browse public resources by topic. Administrative destinations are included for authorized contributors and managers.</p>
                    </div>
                    <div className={sectionStyles.sitemapGrid}>
                      {sitemapGroups.map((group) => (
                        <section className={sectionStyles.sitemapGroup} key={group.topic}>
                          <div className={sectionStyles.sitemapGroupHeading}>
                            <h2>{group.topic}</h2>
                            {group.restricted && <span>PERMISSION REQUIRED</span>}
                            <p>{group.description}</p>
                          </div>
                          <nav aria-label={group.topic}>
                            {group.links.map(([href, label, description]) => (
                              <Link href={href} key={href}>
                                <strong>{label}</strong>
                                <small>{description}</small>
                              </Link>
                            ))}
                          </nav>
                        </section>
                      ))}
                    </div>
                  </div>
                ),
              },
              {
                header: 'About the project',
                content: (
                  <div className={`accordion-interior ${sectionStyles.projectOverview}`}>
                    <div className={sectionStyles.projectLead}>
                      <span>THE SHUFFLEDB PROJECT</span>
                      <h1>A connected reference for understanding, practicing, and preserving shuffle dance.</h1>
                      <p>
                        ShuffleDB documents positions, moves, transitions, simple cycles, combinations, and choreography as related pieces rather than an isolated list of names. Each content item can carry descriptions, alternative names, classifications, physical details, media perspectives, and links to the elements around it.
                      </p>
                    </div>

                    <section className={sectionStyles.projectProblems}>
                      <div className={sectionStyles.projectProblemsIntro}>
                        <span>WHY THIS PROJECT EXISTS</span>
                        <h1>Move collections: common problems.</h1>
                        <p>
                          Shuffle knowledge already exists across many communities and platforms, but finding it, comparing it, and preserving its context remains difficult.
                        </p>
                      </div>
                      <div className={sectionStyles.projectProblemGrid}>
                        <article>
                          <span>01</span>
                          <h2>Social media</h2>
                          <p>
                            Shuffle dancers share excellent free material across social platforms, but useful information is often buried among unrelated posts. Creators may use different names and organizational systems, media quality varies, and valuable content can disappear without warning.
                          </p>
                        </article>
                        <article>
                          <span>02</span>
                          <h2>Commercial collections, courses, and online classes</h2>
                          <p>
                            Paid collections can limit access and depend on the labor and perspective of only one person or a small team. Without a shared platform, dancers must navigate fragmented lessons, inconsistent terminology, and disconnected advice instead of a cohesive reference that benefits from the wider shuffle community.
                          </p>
                        </article>
                      </div>
                    </section>

                    <div className={sectionStyles.projectPillars}>
                      <article>
                        <span>01</span>
                        <h2>Explore the knowledge</h2>
                        <p>Browse the structured library directly or use Shuffle MindMap to see how individual ideas connect to larger movement systems.</p>
                        <div><Link href="/Moves">Browse moves</Link><Link href="/Mindmap">Open Shuffle MindMap</Link></div>
                      </article>
                      <article>
                        <span>02</span>
                        <h2>Learn through structure</h2>
                        <p>Exercises turn reference material into focused practice, while lists and sequences let dancers organize content into useful progressions.</p>
                        <div><Link href="/Exercises">View exercises</Link><Link href="/Sequences">View sequences</Link></div>
                      </article>
                      <article>
                        <span>03</span>
                        <h2>Improve it together</h2>
                        <p>Missions identify specific gaps, guide contributors through consistent requirements, and send proposals through review before changing established content.</p>
                        <div><Link href="/Missions">Take a mission</Link><Link href="/Contributors">Meet contributors</Link></div>
                      </article>
                    </div>

                    <section className={sectionStyles.projectPipeline}>
                      <div>
                        <span>CONNECTED CREATOR PIPELINE</span>
                        <h1>From capture to publication.</h1>
                        <p>
                          The wider ShuffleDB platform connects CapDat for trustworthy capture, TrackDat for movement-aware visuals, EditDat for composition, and PostDat for editorial preparation and publishing. ShuffleMaps adds geographic discovery for scenes, studios, events, classes, and dancers.
                        </p>
                      </div>
                      <nav aria-label="ShuffleDB product tools">
                        <Link href="/CapDat">CapDat</Link>
                        <Link href="/TrackDat">TrackDat</Link>
                        <Link href="/EditDat">EditDat</Link>
                        <Link href="/PostDat">PostDat</Link>
                        <Link href="/ShuffleMaps">ShuffleMaps</Link>
                      </nav>
                    </section>

                    <section className={sectionStyles.sandboxCallout}>
                      <div>
                        <span>DEVELOPMENT WORKBOARD</span>
                        <h1>See what is complete, what is verified, and what comes next.</h1>
                        <p>
                          The ShuffleDB Sandbox is the project’s living technical workboard. It records current product priorities, mission-system phases, production recovery, backups, database integrity, security work, release verification, and the boundaries between the core site and its connected tools. It also links to the dedicated CapDat, TrackDat, EditDat, and PostDat workboards.
                        </p>
                      </div>
                      <Link href="/ShuffleDBSandbox">Open the ShuffleDB Sandbox →</Link>
                    </section>

                    <div className={sectionStyles.projectFooterLinks}>
                      <Link href="/OurVision">Read the project vision</Link>
                      <Link href="/TechnologyStack">Explore the technology stack</Link>
                      <Link href="/Contributors">View contributors</Link>
                    </div>
                  </div>
                ),
              },
            ].sort(
              (first, second) =>
                homepageAccordionOrder.indexOf(first.header) -
                homepageAccordionOrder.indexOf(second.header)
            )}
          />
        </div>
      </div>
    </section>
  </>
);

export async function getServerSideProps(context) {
  const username = ''; // Replace this with actual logic to fetch username.
  const contentCounts = {
    Position: 0,
    Move: 0,
    'Simple Cycle': 0,
    Transition: 0,
    Combination: 0,
    Choreography: 0,
  };

  try {
    const [rows] = await pool.query(
      `SELECT c.type, COUNT(DISTINCT c.content_id) AS total
       FROM content c
       LEFT JOIN missions m ON m.source_content_id = c.content_id
       LEFT JOIN mission_submissions ms ON ms.submitted_content_id = c.content_id
       WHERE c.public_facing = 1 AND m.id IS NULL AND ms.id IS NULL
       GROUP BY c.type`
    );
    rows.forEach((row) => {
      if (Object.prototype.hasOwnProperty.call(contentCounts, row.type)) {
        contentCounts[row.type] = Number(row.total) || 0;
      }
    });
  } catch (error) {
    console.error('Unable to load homepage content counts:', error);
  }

  return {
    props: {
      username,
      contentCounts,
    },
  };
}

export default Home;
